import React from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { ShoppingCart, Package, DollarSign, User, MapPin } from 'lucide-react'

export interface TestScenario {
  id: string
  name: string
  description: string
  category: 'basic' | 'complex' | 'edge-case'
  data: {
    clientId: string
    clientSecret: string
    scope: string
    email: string
    password: string
    merchantId: string
    totalAmount: number
    serviceFees: number
    platformFees: number
    currency: string
    merchantReference: string
    returnUrl: string
    cancelUrl: string
    description: string
    customerName: string
    customerEmail: string
    customerExternalId: string
    billingLine1: string
    billingLine2: string
    billingCity: string
    billingState: string
    billingPostalCode: string
    billingCountry: string
    orderId: string
    lineItems: Array<{
      name: string
      quantity: number
      unitPrice: number
      totalPrice: number
    }>
    metadata: Array<{
      key: string
      value: string
    }>
  }
}

const testScenarios: TestScenario[] = [
  {
    id: 'basic-single-item',
    name: 'Basic Single Item',
    description: 'Simple payment with one item - perfect for basic testing',
    category: 'basic',
    data: {
      clientId: '',
      clientSecret: '',
      scope: 'payment:create payment:read',
      email: '',
      password: '',
      merchantId: '',
      totalAmount: 2300,
      serviceFees: 100,
      platformFees: 200,
      currency: 'USD',
      merchantReference: 'ORDER BASIC 001',
      returnUrl: 'https://merchant.com/payment/success',
      cancelUrl: 'https://merchant.com/payment/cancel',
      description: 'Basic test payment for single item',
      customerName: 'John Smith',
      customerEmail: 'john.smith@example.com',
      customerExternalId: 'CUST_001',
      billingLine1: '123 Main Street',
      billingLine2: 'Apt 4B',
      billingCity: 'New York',
      billingState: 'NY',
      billingPostalCode: '10001',
      billingCountry: 'US',
      orderId: `ORDER_BASIC_${Date.now()}`,
      lineItems: [
        {
          name: 'Concert Ticket',
          quantity: 1,
          unitPrice: 2000,
          totalPrice: 2000,
        },
      ],
      metadata: [
        {
          key: 'campaign_id',
          value: 'SUMMER2024',
        },
        {
          key: 'source',
          value: 'test_checkout',
        },
      ],
    },
  },
  {
    id: 'multiple-line-items',
    name: 'Multiple Line Items',
    description: 'Complex order with multiple items and fees',
    category: 'complex',
    data: {
      clientId: '',
      clientSecret: '',
      scope: 'payment:create payment:read',
      email: '',
      password: '',
      merchantId: '',
      totalAmount: 8000,
      serviceFees: 300,
      platformFees: 200,
      currency: 'USD',
      merchantReference: 'ORDER MULTI 001',
      returnUrl: 'https://merchant.com/payment/success',
      cancelUrl: 'https://merchant.com/payment/cancel',
      description: 'Multiple items order with service and platform fees',
      customerName: 'Sarah Johnson',
      customerEmail: 'sarah.johnson@example.com',
      customerExternalId: 'CUST_002',
      billingLine1: '456 Oak Avenue',
      billingLine2: 'Suite 200',
      billingCity: 'Los Angeles',
      billingState: 'CA',
      billingPostalCode: '90210',
      billingCountry: 'US',
      orderId: `ORDER_MULTI_${Date.now()}`,
      lineItems: [
        {
          name: 'VIP Concert Ticket',
          quantity: 2,
          unitPrice: 2500,
          totalPrice: 5000,
        },
        {
          name: 'Parking Pass',
          quantity: 1,
          unitPrice: 1500,
          totalPrice: 1500,
        },
        {
          name: 'Merchandise Bundle',
          quantity: 1,
          unitPrice: 1000,
          totalPrice: 1000,
        },
      ],
      metadata: [
        {
          key: 'order_type',
          value: 'subscription',
        },
        {
          key: 'customer_tier',
          value: 'premium',
        },
        {
          key: 'promotion_code',
          value: 'SUMMER2024',
        },
        {
          key: 'referral_id',
          value: 'ref_12345',
        },
      ],
    },
  },
  {
    id: 'metadata-rich',
    name: 'Metadata Rich',
    description: 'Order with comprehensive metadata for advanced testing scenarios',
    category: 'complex',
    data: {
      clientId: '',
      clientSecret: '',
      scope: 'payment:create payment:read',
      email: '',
      password: '',
      merchantId: '',
      totalAmount: 13800,
      serviceFees: 1200,
      platformFees: 600,
      currency: 'USD',
      merchantReference: 'ORDER META 001',
      returnUrl: 'https://merchant.com/payment/success',
      cancelUrl: 'https://merchant.com/payment/cancel',
      description: 'Complex order with rich metadata for testing',
      customerName: 'Michael Chen',
      customerEmail: 'michael.chen@example.com',
      customerExternalId: 'CUST_003',
      billingLine1: '789 Pine Street',
      billingLine2: 'Floor 3',
      billingCity: 'San Francisco',
      billingState: 'CA',
      billingPostalCode: '94102',
      billingCountry: 'US',
      orderId: `ORDER_META_${Date.now()}`,
      lineItems: [
        {
          name: 'Premium Event Package',
          quantity: 1,
          unitPrice: 12000,
          totalPrice: 12000,
        },
      ],
      metadata: [
        {
          key: 'campaign_id',
          value: 'WINTER2024',
        },
        {
          key: 'source_platform',
          value: 'mobile_app',
        },
        {
          key: 'customer_tier',
          value: 'vip',
        },
        {
          key: 'ab_test_group',
          value: 'variant_a',
        },
        {
          key: 'user_segment',
          value: 'high_value_customer',
        },
        {
          key: 'payment_method_pref',
          value: 'credit_card',
        },
        {
          key: 'referral_source',
          value: 'email_campaign',
        },
        {
          key: 'device_type',
          value: 'mobile',
        },
      ],
    },
  },
]

interface TestScenariosProps {
  onLoadScenario: (scenario: TestScenario['data']) => void
}

export function TestScenarios({ onLoadScenario }: TestScenariosProps) {
  const getCategoryIcon = (category: TestScenario['category']) => {
    switch (category) {
      case 'basic':
        return <ShoppingCart className="h-4 w-4" />
      case 'complex':
        return <Package className="h-4 w-4" />
      case 'edge-case':
        return <DollarSign className="h-4 w-4" />
      default:
        return <ShoppingCart className="h-4 w-4" />
    }
  }

  const getCategoryColor = (category: TestScenario['category']) => {
    switch (category) {
      case 'basic':
        return 'bg-green-100 text-green-800'
      case 'complex':
        return 'bg-blue-100 text-blue-800'
      case 'edge-case':
        return 'bg-orange-100 text-orange-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleLoadScenario = (scenario: TestScenario) => {
    // Generate a new order ID with timestamp to avoid conflicts
    const scenarioData = {
      ...scenario.data,
      orderId: `${scenario.data.orderId}_${Date.now()}`,
    }
    onLoadScenario(scenarioData)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Available Test Scenarios</h3>
        <Badge variant="secondary">{testScenarios.length} scenarios</Badge>
      </div>

      <div className="grid gap-4">
        {testScenarios.map((scenario) => (
          <Card key={scenario.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {getCategoryIcon(scenario.category)}
                  <div>
                    <CardTitle className="text-base">{scenario.name}</CardTitle>
                    <CardDescription className="text-sm mt-1">{scenario.description}</CardDescription>
                  </div>
                </div>
                <Badge className={getCategoryColor(scenario.category)}>{scenario.category}</Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-3">
                {/* Order Summary */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <DollarSign className="h-4 w-4" />
                  <span>
                    Total: ${scenario.data.totalAmount.toFixed(2)} | Items: {scenario.data.lineItems.length} | Currency:{' '}
                    {scenario.data.currency}
                  </span>
                </div>

                {/* Customer Info */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <User className="h-4 w-4" />
                  <span>
                    {scenario.data.customerName} ({scenario.data.customerEmail})
                  </span>
                </div>

                {/* Billing Address */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span>
                    {scenario.data.billingCity}, {scenario.data.billingState} {scenario.data.billingPostalCode}
                  </span>
                </div>

                {/* Line Items Preview */}
                <div className="text-sm text-muted-foreground">
                  <span className="font-medium">Items:</span>{' '}
                  {scenario.data.lineItems.map((item, index) => (
                    <span key={index}>
                      {item.quantity}x {item.name}
                      {index < scenario.data.lineItems.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </div>

                {/* Metadata Preview */}
                {scenario.data.metadata && scenario.data.metadata.length > 0 && (
                  <div className="text-sm text-muted-foreground">
                    <span className="font-medium">Metadata:</span>{' '}
                    {scenario.data.metadata.slice(0, 3).map((meta, index) => (
                      <span key={index}>
                        {meta.key}={meta.value}
                        {index < Math.min(3, scenario.data.metadata.length - 1) ? ', ' : ''}
                      </span>
                    ))}
                    {scenario.data.metadata.length > 3 && <span> +{scenario.data.metadata.length - 3} more</span>}
                  </div>
                )}

                <Button onClick={() => handleLoadScenario(scenario)} className="w-full" variant="outline">
                  Load This Scenario
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-6 p-4 bg-muted rounded-lg">
        <h4 className="font-medium text-sm mb-2">How to use test scenarios:</h4>
        <ul className="text-sm text-muted-foreground space-y-1">
          <li>• Click "Load This Scenario" to populate the form with test data</li>
          <li>• Review and modify the data as needed</li>
          <li>• Submit the form to test the payment intent creation</li>
          <li>• Each scenario includes realistic test data for comprehensive testing</li>
        </ul>
      </div>
    </div>
  )
}
